import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { mainCta } from '@/mocks/cta';

interface HeroProps {
  currentLang: string;
}

export default function Hero({ currentLang }: HeroProps) {
  const { t } = useTranslation('home');
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.open(`${mainCta.mainSite}/search?q=${encodeURIComponent(searchQuery)}`, '_blank', 'noopener,nofollow');
    }
  };

  return (
    <section className="relative min-h-[620px] md:min-h-[720px] flex items-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Stylish%20streetwear%20outfit%20display%20with%20sneakers%20hoodie%20and%20accessories%20arranged%20against%20a%20warm%20terracotta%20wall%20urban%20editorial%20fashion%20photography%20vibrant%20warm%20colors%20dramatic%20soft%20lighting%20high%20detail%20premium%20streetwear%20aesthetic&width=1600&height=900&seq=hero-bg-001&orientation=landscape&nocache=true"
          alt="AllChinaBuy Spreadsheet Fashion Products"
          className="w-full h-full object-cover"
        />
        <div className="hero-overlay absolute inset-0" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-10 xl:px-16 py-20 md:py-28">
        <div className="max-w-4xl mx-auto text-center">
          {/* Eyebrow */}
          <p className="text-accent-400 text-xs md:text-sm font-semibold uppercase tracking-[0.15em] mb-4 md:mb-5">
            {t('hero_eyebrow')}
          </p>

          {/* H1 */}
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold text-background-50 leading-tight mb-5 md:mb-6">
            {t('hero_h1')}
          </h1>

          {/* Subheadline */}
          <p className="text-background-50/80 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-8 md:mb-10">
            {t('hero_subheadline')}
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 mb-8 md:mb-10">
            <Link
              to={`/${currentLang}/allchinabuy-spreadsheet/`}
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary-500 text-background-50 font-label font-semibold text-sm rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('hero_cta_primary')}
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to={`/${currentLang}/guides/`}
              className="inline-flex items-center gap-2 px-7 py-3.5 border border-background-50/30 text-background-50 font-label font-semibold text-sm rounded-md hover:bg-background-50/10 transition-colors whitespace-nowrap"
            >
              {t('hero_cta_secondary')}
            </Link>
          </div>

          {/* Trust microcopy */}
          <p className="text-background-50/50 text-xs md:text-sm mb-10 md:mb-12">
            {t('hero_trust')}
          </p>

          {/* Search Module */}
          <div className="max-w-2xl mx-auto">
            <p className="text-background-50/70 text-sm font-medium mb-3">
              {t('search_title')}
            </p>
            <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 relative">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('search_placeholder')}
                  className="w-full pl-10 pr-4 py-3 bg-background-50/95 rounded-md text-foreground-800 placeholder:text-foreground-400 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400/50 border-0"
                />
              </div>
              <button
                type="submit"
                className="px-6 py-3 bg-accent-500 text-background-50 font-label font-semibold text-sm rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                {t('search_button')}
              </button>
            </form>
            <div className="mt-3 text-background-50/40 text-xs">
              <span>{t('search_popular')}:</span>{' '}
              <span className="text-background-50/60">{t('search_popular_items')}</span>
            </div>
          </div>

          {/* Main CTA Buttons to Main Site */}
          <div className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={mainCta.mainSite}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-50/15 backdrop-blur-sm text-background-50 font-label font-semibold text-sm rounded-md border border-background-50/20 hover:bg-background-50/25 transition-colors whitespace-nowrap"
            >
              <i className="ri-external-link-line"></i>
              {t('btn_main_site')}
            </a>
            <a
              href={mainCta.mainSite}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-500/90 text-background-50 font-label font-semibold text-sm rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-shopping-cart-line"></i>
              {t('btn_buy_now')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}